import { freightFormMessages } from '../components/freight-browser/components/freight-form/freight-form-messages';
import { US } from '../constants/country-codes';

import type { FreightPackagingType } from '../types/ltl';
import type { MessageDescriptor } from 'react-intl';

export const FREIGHT_CLASS_OPTIONS = [
  { value: '_50', label: '50' },
  { value: '_55', label: '55' },
  { value: '_60', label: '60' },
  { value: '_65', label: '65' },
  { value: '_70', label: '70' },
  { value: '_77_5', label: '77.5' },
  { value: '_85', label: '85' },
  { value: '_92_5', label: '92.5' },
  { value: '_100', label: '100' },
  { value: '_110', label: '110' },
  { value: '_125', label: '125' },
  { value: '_150', label: '150' },
  { value: '_175', label: '175' },
  { value: '_200', label: '200' },
  { value: '_250', label: '250' },
  { value: '_300', label: '300' },
  { value: '_400', label: '400' },
  { value: '_500', label: '500' },
];

type PackagingTypeOption = {
  value: FreightPackagingType;
  label: MessageDescriptor;
};

export const PACKAGING_TYPE_OPTIONS: PackagingTypeOption[] = [
  { value: 'Bag', label: freightFormMessages.packagingBag },
  { value: 'Bale', label: freightFormMessages.packagingBale },
  { value: 'Box', label: freightFormMessages.packagingBox },
  { value: 'Bundle', label: freightFormMessages.packagingBundle },
  { value: 'Carton', label: freightFormMessages.packagingCarton },
  { value: 'Case', label: freightFormMessages.packagingCase },
  { value: 'Crate', label: freightFormMessages.packagingCrate },
  { value: 'Cylinder', label: freightFormMessages.packagingCylinder },
  { value: 'Drum', label: freightFormMessages.packagingDrum },
  { value: 'Pail', label: freightFormMessages.packagingPail },
  { value: 'Pallet', label: freightFormMessages.packagingPallet },
  { value: 'Pieces', label: freightFormMessages.packagingPieces },
  { value: 'Reel', label: freightFormMessages.packagingReel },
  { value: 'Roll', label: freightFormMessages.packagingRoll },
  { value: 'Skid', label: freightFormMessages.packagingSkid },
  { value: 'Tank', label: freightFormMessages.packagingTank },
  { value: 'Tote', label: freightFormMessages.packagingTote },
  { value: 'Trailer', label: freightFormMessages.packagingTrailer },
  { value: 'Tube', label: freightFormMessages.packagingTube },
];

export const LTL_SUPPORTED_COUNTRY_CODES: readonly string[] = [US];

export const LIMITED_ACCESS = 'LimitedAccess';

/** Error code sent on the `freight-book` Pusher message when a booking is blocked by insufficient inventory. */
export const FREIGHT_INSUFFICIENT_INVENTORY_CODE = 'INSUFFICIENT_INVENTORY';
export const LOCATION_TYPE_OPTIONS = [
  { value: 'Commercial', label: 'Commercial' },
  { value: 'Residential', label: 'Residential' },
  { value: 'Construction', label: 'Construction Site' },
  { value: LIMITED_ACCESS, label: 'Limited Access' },
  { value: 'Tradeshow', label: 'Trade Show' },
  { value: 'Airport', label: 'Airport' },
  { value: 'PierPortWarf', label: 'Pier/Port/Wharf' },
  { value: 'GovernmentFacility', label: 'Government Facility' },
  { value: 'DistributionCenter', label: 'Distribution Center' },
  { value: 'ContainerFreightStation', label: 'Container Freight Station' },
  { value: 'SecuredLocation', label: 'Secured Location' },
];

export const ACCESSORIAL_GROUPS = [
  {
    groupKey: 'pickup',
    services: [
      { field: 'isLiftgatePickup', messageKey: 'liftgatePickup' },
      { field: 'isInsidePickup', messageKey: 'insidePickup' },
      { field: 'isResidentialPickup', messageKey: 'residentialPickup' },
      { field: 'isLimitedAccessPickup', messageKey: 'limitedAccessPickup' },
      { field: 'isFlatbedPickup', messageKey: 'flatbedPickup' },
      { field: 'isDockLevelPickup', messageKey: 'dockLevelPickup' },
      { field: 'isTopLoadPickup', messageKey: 'topLoadPickup' },
      { field: 'isConstructionSitePickup', messageKey: 'constructionSitePickup' },
      { field: 'isTradeShowPickup', messageKey: 'tradeShowPickup' },
      { field: 'isNotifyBeforePickup', messageKey: 'notifyBeforePickup' },
    ],
  },
  {
    groupKey: 'delivery',
    services: [
      { field: 'isLiftgateDelivery', messageKey: 'liftgateDelivery' },
      { field: 'isInsideDelivery', messageKey: 'insideDelivery' },
      { field: 'isResidentialDelivery', messageKey: 'residentialDelivery' },
      { field: 'isLimitedAccessDelivery', messageKey: 'limitedAccessDelivery' },
      { field: 'isFlatbedDelivery', messageKey: 'flatbedDelivery' },
      { field: 'isDockLevelDelivery', messageKey: 'dockLevelDelivery' },
      { field: 'isConstructionSiteDelivery', messageKey: 'constructionSiteDelivery' },
      { field: 'isTradeShowDelivery', messageKey: 'tradeShowDelivery' },
      { field: 'isAppointmentDelivery', messageKey: 'appointmentDelivery' },
      { field: 'isCallBeforeDelivery', messageKey: 'callBeforeDelivery' },
      { field: 'isSortAndSegregate', messageKey: 'sortAndSegregate' },
      { field: 'isNotifyBeforeDelivery', messageKey: 'notifyBeforeDelivery' },
    ],
  },
  {
    groupKey: 'commodity',
    services: [
      { field: 'isHazmat', messageKey: 'hazmatService' },
      { field: 'isTemperatureControl', messageKey: 'temperatureControl' },
      { field: 'isProtectFromFreezing', messageKey: 'protectFromFreezing' },
      { field: 'isOverlength8to12ft', messageKey: 'overlength8to12' },
      { field: 'isOverlength12to20ft', messageKey: 'overlength12to20' },
      { field: 'isOverlength20ftPlus', messageKey: 'overlength20Plus' },
      { field: 'isGuaranteedDelivery', messageKey: 'guaranteedDelivery' },
      { field: 'isBlindShipment', messageKey: 'blindShipment' },
      { field: 'isMarkAndTag', messageKey: 'markAndTag' },
      { field: 'isCOD', messageKey: 'cod' },
    ],
  },
] as const;

export const HAZMAT_CLASS_OPTIONS = [
  { value: 'Class1_1A', label: 'Class 1 (1.1A) - Explosives' },
  { value: 'Class1_1B', label: 'Class 1 (1.1B) - Explosives' },
  { value: 'Class1_1C', label: 'Class 1 (1.1C) - Explosives' },
  { value: 'Class1_1D', label: 'Class 1 (1.1D) - Explosives' },
  { value: 'Class1_1E', label: 'Class 1 (1.1E) - Explosives' },
  { value: 'Class1_1F', label: 'Class 1 (1.1F) - Explosives' },
  { value: 'Class1_1G', label: 'Class 1 (1.1G) - Explosives' },
  { value: 'Class1_1H', label: 'Class 1 (1.1H) - Explosives' },
  { value: 'Class1_1J', label: 'Class 1 (1.1J) - Explosives' },
  { value: 'Class1_1K', label: 'Class 1 (1.1K) - Explosives' },
  { value: 'Class1_1L', label: 'Class 1 (1.1L) - Explosives' },
  { value: 'Class1_2B', label: 'Class 1 (1.2B) - Explosives' },
  { value: 'Class1_2C', label: 'Class 1 (1.2C) - Explosives' },
  { value: 'Class1_2D', label: 'Class 1 (1.2D) - Explosives' },
  { value: 'Class1_2E', label: 'Class 1 (1.2E) - Explosives' },
  { value: 'Class1_2F', label: 'Class 1 (1.2F) - Explosives' },
  { value: 'Class1_2G', label: 'Class 1 (1.2G) - Explosives' },
  { value: 'Class1_2H', label: 'Class 1 (1.2H) - Explosives' },
  { value: 'Class1_2J', label: 'Class 1 (1.2J) - Explosives' },
  { value: 'Class1_2K', label: 'Class 1 (1.2K) - Explosives' },
  { value: 'Class1_2L', label: 'Class 1 (1.2L) - Explosives' },
  { value: 'Class1_3C', label: 'Class 1 (1.3C) - Explosives' },
  { value: 'Class1_3F', label: 'Class 1 (1.3F) - Explosives' },
  { value: 'Class1_3G', label: 'Class 1 (1.3G) - Explosives' },
  { value: 'Class1_3H', label: 'Class 1 (1.3H) - Explosives' },
  { value: 'Class1_3J', label: 'Class 1 (1.3J) - Explosives' },
  { value: 'Class1_3K', label: 'Class 1 (1.3K) - Explosives' },
  { value: 'Class1_3L', label: 'Class 1 (1.3L) - Explosives' },
  { value: 'Class1_4B', label: 'Class 1 (1.4B) - Explosives' },
  { value: 'Class1_4C', label: 'Class 1 (1.4C) - Explosives' },
  { value: 'Class1_4D', label: 'Class 1 (1.4D) - Explosives' },
  { value: 'Class1_4E', label: 'Class 1 (1.4E) - Explosives' },
  { value: 'Class1_4F', label: 'Class 1 (1.4F) - Explosives' },
  { value: 'Class1_4G', label: 'Class 1 (1.4G) - Explosives' },
  { value: 'Class1_4S', label: 'Class 1 (1.4S) - Explosives' },
  { value: 'Class1_5D', label: 'Class 1 (1.5D) - Explosives' },
  { value: 'Class1_6N', label: 'Class 1 (1.6N) - Explosives' },
  { value: 'Class2_1', label: 'Class 2.1 - Flammable Gas' },
  { value: 'Class2_2', label: 'Class 2.2 - Non-Flammable Gas' },
  { value: 'Class2_3', label: 'Class 2.3 - Toxic Gas' },
  { value: 'Class3', label: 'Class 3 - Flammable Liquids' },
  { value: 'Class4_1', label: 'Class 4.1 - Flammable Solids' },
  { value: 'Class4_2', label: 'Class 4.2 - Spontaneously Combustible' },
  { value: 'Class4_3', label: 'Class 4.3 - Dangerous When Wet' },
  { value: 'Class5_1', label: 'Class 5.1 - Oxidizers' },
  { value: 'Class5_2', label: 'Class 5.2 - Organic Peroxides' },
  { value: 'Class6_1', label: 'Class 6.1 - Toxic Substances' },
  { value: 'Class6_2', label: 'Class 6.2 - Infectious Substances' },
  { value: 'Class7', label: 'Class 7 - Radioactive Materials' },
  { value: 'Class8', label: 'Class 8 - Corrosives' },
  { value: 'Class9', label: 'Class 9 - Miscellaneous Dangerous Goods' },
];

export const PACKING_GROUP_OPTIONS = [
  { value: 'PGI', label: 'I - Great Danger' },
  { value: 'PGII', label: 'II - Medium Danger' },
  { value: 'PGIII', label: 'III - Minor Danger' },
  { value: 'NoPackagingGroup', label: 'No Packaging Group' },
];

export const HAZMAT_ID_TYPE_OPTIONS = [
  { value: 'UN', label: 'UN' },
  { value: 'NA', label: 'NA' },
];

export const INSURANCE_COMMODITY_CATEGORY_OPTIONS = [
  { value: 'GeneralMerchandise', label: 'General Merchandise' },
  { value: 'Equipment', label: 'Equipment' },
  { value: 'FoodBeverages', label: 'Food & Beverages' },
  { value: 'ChemicalsPaint', label: 'Chemicals & Paint' },
  { value: 'Electronics', label: 'Electronics' },
  { value: 'Construction', label: 'Construction' },
  { value: 'Furniture', label: 'Furniture' },
  { value: 'Stonework', label: 'Stonework' },
  { value: 'FragileItems', label: 'Fragile Items' },
  { value: 'Perishables', label: 'Perishables' },
  { value: 'Jewelry', label: 'Jewelry' },
  { value: 'MotorizedTransport', label: 'Motorized Transport' },
  { value: 'ArtAntiques', label: 'Art & Antiques' },
  { value: 'Explosives', label: 'Explosives' },
  { value: 'LifeForms', label: 'Life Forms' },
  { value: 'ControlledItems', label: 'Controlled Items' },
  { value: 'PharmaNonRefrigerated', label: 'Pharma (Non-Refrigerated)' },
  { value: 'PharmaRefrigerated', label: 'Pharma (Refrigerated)' },
];

export const INSURANCE_CONDITION_OPTIONS = [
  { value: 'New', label: 'New' },
  { value: 'Used', label: 'Used' },
];

/**
 * Converts a FreightClass enum name string to a human-readable display string.
 * e.g. '_50' → '50', '_77_5' → '77.5'
 */
export const displayFreightClass = (freightClass: string): string => freightClass?.replace(/^_/, '').replace(/_/g, '.');

export const FREIGHT_PROVIDER_ID = {
  Wwex: 1,
  Unishippers: 2,
} as const;
export type FreightProviderId = (typeof FREIGHT_PROVIDER_ID)[keyof typeof FREIGHT_PROVIDER_ID];

export const FREIGHT_QUOTE_ERROR_CODE = {
  QuoteIneligible: 'quote_ineligible',
} as const;
export type FreightQuoteErrorCode = (typeof FREIGHT_QUOTE_ERROR_CODE)[keyof typeof FREIGHT_QUOTE_ERROR_CODE];

export const FREIGHT_MAX_CARRIER_REFERENCES = 5;

export const FREIGHT_CUSTOM_ATTRIBUTE_MAX_LENGTH = 35;
